<template>
  <div class="customers-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <div class="hero-section">
      <div class="hero-background">
        <img :src="heroBackgroundImage" alt="Customers Background" />
        <div class="slide-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">客户案例</h1>
        <p class="page-subtitle">与全球知名企业合作，共创价值</p>
      </div>
    </div>
    
    <div class="content-section">
      <div class="container">
        <div class="customers-intro">
          <h2 class="section-title">我们的客户</h2>
          <p class="section-description">
            我们为各行各业的客户提供优质的产品和服务，帮助他们提升生产效率，
            降低成本，实现可持续发展。
          </p>
        </div>
        
        <div class="customer-categories">
          <div class="category-tabs">
            <button 
              v-for="category in categories" 
              :key="category.id"
              :class="['category-tab', { active: activeCategory === category.id }]"
              @click="activeCategory = category.id"
            >
              {{ category.name }}
            </button>
          </div>
          
          <div class="customers-grid">
            <div
              class="customer-card"
              v-for="customer in filteredCustomers"
              :key="customer.id"
              :style="{ backgroundImage: `url(${customer.backgroundImage})` }"
            >
              <div class="card-overlay"></div>
              <div class="card-content">
                <div class="customer-logo">
                  <img :src="customer.logo" :alt="customer.name" />
                </div>
                <h3 class="customer-name">{{ customer.name }}</h3>
                <p class="customer-industry">{{ customer.industry }}</p>
                <p class="customer-description">{{ customer.description }}</p>
                <div class="customer-results">
                  <h4>合作成果</h4>
                  <ul>
                    <li v-for="result in customer.results" :key="result">{{ result }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="testimonials">
          <h2 class="section-title">客户评价</h2>
          <div class="testimonials-grid">
            <div class="testimonial-card" v-for="testimonial in testimonials" :key="testimonial.id">
              <div class="testimonial-content">
                <p class="testimonial-text">"{{ testimonial.text }}"</p>
              </div>
              <div class="testimonial-author">
                <div class="author-avatar">
                  <img :src="testimonial.avatar" :alt="testimonial.name" />
                </div>
                <div class="author-info">
                  <h4 class="author-name">{{ testimonial.name }}</h4>
                  <p class="author-position">{{ testimonial.position }}</p>
                  <p class="author-company">{{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const heroBackgroundImage = new URL('@/assets/carousel/slide1.webp', import.meta.url).href

interface Category {
  id: number
  name: string
}

interface Customer {
  id: number
  name: string
  industry: string
  description: string
  logo: string
  categoryId: number
  results: string[]
  backgroundImage: string
}

interface Testimonial {
  id: number
  name: string
  position: string
  company: string
  text: string
  avatar: string
}

const activeCategory = ref(1)

const categories = ref<Category[]>([
  { id: 1, name: "全部客户" },
  { id: 2, name: "制造业" },
  { id: 3, name: "汽车工业" },
  { id: 4, name: "电子行业" },
  { id: 5, name: "航空航天" }
])

const customers = ref<Customer[]>([
  {
    id: 1,
    name: "精密制造集团",
    industry: "制造业",
    description: "全球领先的精密制造企业，专注于高精度零部件的生产和加工。",
    logo: "/src/assets/assets/svg/system.svg",
    categoryId: 2,
    results: [
      "生产效率提升35%",
      "产品合格率达到99.8%",
      "运营成本降低20%"
    ],
    backgroundImage: new URL('@/assets/products-center/Machine Parts.png', import.meta.url).href
  },
  {
    id: 2,
    name: "汽车零部件公司",
    industry: "汽车工业",
    description: "专业从事汽车零部件研发、生产和销售的大型企业集团。",
    logo: "/src/assets/assets/car.png",
    categoryId: 3,
    results: [
      "生产线自动化程度提升40%",
      "产品交付周期缩短25%",
      "质量投诉率降低60%"
    ],
    backgroundImage: new URL('@/assets/products-center/Penumatic Actuators.webp', import.meta.url).href
  },
  {
    id: 3,
    name: "电子科技股份",
    industry: "电子行业",
    description: "专注于电子元器件研发和制造的高新技术企业。",
    logo: "/src/assets/assets/svg/laptop.svg",
    categoryId: 4,
    results: [
      "产品精度提升至微米级",
      "生产良品率提升15%",
      "设备故障率降低50%"
    ],
    backgroundImage: new URL('@/assets/products-center/Pneumatic Controllers.png', import.meta.url).href
  },
  {
    id: 4,
    name: "航空航天工业",
    industry: "航空航天",
    description: "专业从事航空器和航天器零部件制造的大型国有企业。",
    logo: "/src/assets/assets/svg/airplane.svg",
    categoryId: 5,
    results: [
      "产品符合国际航空标准",
      "生产效率提升30%",
      "质量检测效率提升45%"
    ],
    backgroundImage: new URL('@/assets/products-center/E-Motion products.png', import.meta.url).href
  },
  {
    id: 5,
    name: "智能装备制造",
    industry: "制造业",
    description: "专业从事智能制造装备研发和生产的高科技企业。",
    logo: "/src/assets/assets/svg/hot.svg",
    categoryId: 2,
    results: [
      "设备智能化水平大幅提升",
      "人工成本降低35%",
      "产品一致性显著改善"
    ],
    backgroundImage: new URL('@/assets/products-center/Customerized Solutions.png', import.meta.url).href
  },
  {
    id: 6,
    name: "新能源汽车集团",
    industry: "汽车工业",
    description: "专注于新能源汽车研发和生产的新兴企业集团。",
    logo: "/src/assets/assets/svg/shop.svg",
    categoryId: 3,
    results: [
      "生产线灵活性提升50%",
      "新产品开发周期缩短30%",
      "能源利用效率提升25%"
    ],
    backgroundImage: new URL('@/assets/products-center/Pneumatic Accessories.png', import.meta.url).href
  }
])

const testimonials = ref<Testimonial[]>([
  {
    id: 1,
    name: "张工程师",
    position: "技术总监",
    company: "精密制造集团",
    text: "与贵公司合作以来，我们的生产效率得到了显著提升，产品质量也更加稳定。他们的技术支持和售后服务非常专业。",
    avatar: "/src/assets/assets/login/avatar.svg"
  },
  {
    id: 2,
    name: "李经理",
    position: "生产总监",
    company: "汽车零部件公司",
    text: "贵公司的产品帮助我们实现了生产线的自动化升级，不仅提高了生产效率，还大大降低了人工成本。非常感谢他们的专业服务。",
    avatar: "/src/assets/assets/login/illustration.svg"
  },
  {
    id: 3,
    name: "王博士",
    position: "研发主管",
    company: "电子科技股份",
    text: "贵公司的精密控制产品为我们提供了完美的解决方案，帮助我们在激烈的市场竞争中保持了技术领先优势。",
    avatar: "/src/assets/assets/user.jpg"
  }
])

const filteredCustomers = computed(() => {
  if (activeCategory.value === 1) {
    return customers.value
  }
  return customers.value.filter(customer => customer.categoryId === activeCategory.value)
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.customers-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 20px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.content-section {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.customers-intro {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 36px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Bitter', serif;
}

.section-description {
  font-size: 18px;
  color: #7f8c8d;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Hind', sans-serif;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 50px;
  flex-wrap: wrap;
}

.category-tab {
  padding: 12px 24px;
  border: none;
  background: white;
  color: #7f8c8d;
  font-size: 16px;
  font-weight: 500;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-family: 'Hind', sans-serif;
}

.category-tab:hover {
  color: #9C27B0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.category-tab.active {
  background: linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(156, 39, 176, 0.3);
}

.customers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 80px;
}

.customer-card {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.customer-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.75));
  transition: all 0.4s ease;
}

.customer-card:hover .card-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85));
}

.card-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 35px;
  text-align: center;
}

.customer-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  background: rgba(217, 127, 62, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.95;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.customer-card:hover .customer-logo {
  transform: scale(1.15) translateY(-5px);
}

.customer-logo img {
  width: 50px;
  height: 50px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.customer-name {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.4;
  font-family: 'Bitter', serif;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
  transition: transform 0.3s;
}

.customer-card:hover .customer-name {
  transform: translateY(-3px);
}

.customer-industry {
  font-size: 16px;
  color: #d97f3e;
  font-weight: 500;
  text-align: center;
  margin-bottom: 20px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.customer-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 25px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  font-family: 'Hind', sans-serif;
}

.customer-results h4 {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 15px;
  font-family: 'Bitter', serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.customer-results ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.customer-results li {
  padding: 6px 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  padding-left: 25px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  font-family: 'Hind', sans-serif;
}

.customer-results li:before {
  content: "✓";
  color: #d97f3e;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.testimonials {
  margin-top: 60px;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.testimonial-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.testimonial-card:hover {
  transform: translateY(-5px);
}

.testimonial-text {
  font-size: 16px;
  color: #7f8c8d;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 25px;
}

.testimonial-author {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  flex-shrink: 0;
}

.author-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.author-info {
  flex: 1;
}

.author-name {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif;
}

.author-position {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 3px;
}

.author-company {
  font-size: 14px;
  color: #9C27B0;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .customers-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .content-section {
    padding: 70px 0;
  }

  .customers-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .customer-card {
    height: 300px;
  }

  .page-title {
    font-size: 38px;
  }

  .page-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .section-title {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 16px;
  }
  
  .category-tabs {
    gap: 10px;
  }
  
  .category-tab {
    padding: 10px 20px;
    font-size: 14px;
  }
  
  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}
</style>